/**
 * TypeScript interfaces for Coding Data API responses
 */

import { SavedDiagnosisState } from './diagnosisState.types';
import { MedicalData } from './medicalData.types';

// Coding Tags
export const ALL_CODING_TAG_KEYS = [
  'EMR_filled',
  'archived',
  'has_multiple_chronic_conditions',
  'face_to_face_attached',
  'face_to_face_signed',
  'home_health_order_attached',
  'home_health_order_signed',
  'no_documents',
  'eval_scheduled',
  'eval_completed',
  'medication_profile_complete',
  'new_documents_available',
] as const;
export type CodingTagTypes = (typeof ALL_CODING_TAG_KEYS)[number];
// ready_for_coding is a composite tag sent to the API but not stored in URL/form state
export type CodingTags = Partial<
  Record<CodingTagTypes | 'ready_for_coding', boolean>
>;

// Onset/Exacerbation type
export type OEType = 'Onset' | 'Exacerbation' | null;

// Severity type (0-4 scale)
export type SeverityType = '0' | '1' | '2' | '3' | '4' | null;

// Diagnosis Code types
export interface DiagnosisCode {
  name: string;
  desc: string;
  severity?: SeverityType;
  oe?: OEType;
  onset_date?: string | null; // Format: MM/DD/YYYY
}

export interface DiagnosisWithReasoning {
  diagnosis: DiagnosisCode;
  reasoning: string;
}

export interface PrimaryDiagnosis {
  diagnosis: DiagnosisCode;
  reasoning: string;
}

export interface SecondaryDiagnoses {
  reasoning: string;
  diagnoses: DiagnosisWithReasoning[];
}

// Post Coding Check types
export interface PostCodingCheck {
  active: boolean;
  title: string;
  user_message: string;
  severity: 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
}

export interface PostCodingChecks {
  missing_history_and_physical: PostCodingCheck;
  missing_authorized_diagnoses: PostCodingCheck;
  missing_face_to_face: PostCodingCheck;
  unauthorized_primary_diagnosis: PostCodingCheck;
  similar_primary_diagnosis: PostCodingCheck;
  incompatible_dx_and_care: PostCodingCheck;
  unspecified_wound_diagnosis: PostCodingCheck;
  missing_medication_treatment: PostCodingCheck;
  visit_already_submitted: PostCodingCheck;
  // Active when Roger skipped password-protected referral PDFs.
  password_protected_documents?: PostCodingCheck;
}

// Case Mix types
export interface CaseMixData {
  hipps: string;
  clinical_group_func_level: string;
  admission_source_timing: string;
  comorbidity_adjustment: number;
  case_mix_weight: number;
  lupa_threshold: number;
}

// Visit Information
export interface CodingVisitInfo {
  patient_id: string;
  patient_name_hash?: string;
  patient_first_name: string;
  patient_last_name: string;
  visit_date_date: string;
  /**
   * Raw M0090 (Date Assessment Completed), YYYY-MM-DD or null. For
   * display only: it is NOT the coding as-of date — use the response's
   * top-level `codes_as_of`.
   */
  m0090_date?: string | null;
  visit_type: string;
  nurse_first_name?: string;
  nurse_last_name?: string;
  nurse_username?: string;
  location?: string;
  branch_id?: string | null;
}

// Validation entry for is_signed check
export interface IsSignedValidation {
  value: boolean;
  reasoning: string;
}

// Validations object within coding extraction items
export interface CodingExtractionValidations {
  is_signed?: IsSignedValidation;
}

// Coding Extraction types (used in Intake workflow)
export interface CodingExtractionItem {
  face_to_face_visit_summary?: string;
  home_health_order?: string;
  authorized_diagnoses?: string;
  source: Record<string, string[]>;
  reasoning: string;
  validations?: CodingExtractionValidations;
}

export interface ChecklistItemDetails {
  items: CodingExtractionItem[];
  notQualifyingReasoning?: string | null;
  // For "Signed" fields, filter to only show items where is_signed.value is true
  showOnlySignedItems?: boolean;
  /** When set, show this in the popover instead of AI extraction (e.g. "Manually marked as present at ...") */
  manualOverrideMessage?: string | null;
}

// Main Coding Data Response
export interface CodingDataResponse {
  id: string;
  name: string;
  status: string;
  raw_status?: string;
  /**
   * The coding as-of date (M0090, or the visit date), YYYY-MM-DD.
   * Top level, beside `visit`; present and non-blank on every `ready`
   * response (any raw_status), absent on `in_progress`. Codes are offered and
   * judged on this date, not on `visit.visit_date_date`.
   */
  codes_as_of?: string;
  visit?: CodingVisitInfo;
  patient_data?: MedicalData;
  post_coding_checks?: PostCodingChecks;
  primary_diagnosis?: PrimaryDiagnosis;
  secondary_diagnoses?: SecondaryDiagnoses;
  valid_primary_diagnoses?: DiagnosisWithReasoning[];
  case_mix_data?: CaseMixData;
  // Projected case mix for the 2nd 30-day billing period. Present only when the
  // medical_coding_show_2nd_30_day_billing_period flag is on and the first
  // period is "early"; absent otherwise.
  case_mix_data_2nd_period?: CaseMixData;
  states?: SavedDiagnosisState[];
  tags?: CodingTags;
  scrape_datetime?: string | null;
}
